import PlanningDashboard from './planningView/PlanningDashboard';
import { useParams, useSearchParams, useLocation } from 'react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_IAP_PARAMS,
  validateAndCreateIapSearchParams,
  type IapParams,
} from 'utils/iapParamValidation';
import {
  formsViewToMode,
  resolveFormsView,
  serializeFormsView,
  type FormsView,
} from 'utils/iapFormsView';
import { incrementParamsForUI } from './utils/IapUtils';
import { useIapParams } from './hooks/useIapParams';
import { useICSContext } from './planningView/ICSContext';
import { useCurrentPeriod } from './hooks/incidentHooks';

const IAPDashboardNew = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { periodNumber } = useCurrentPeriod();
  const currentPeriodNumber = periodNumber ?? 0;

  const { targetPhase, setTargetPhase, setMode, formsView, setFormsView } =
    useICSContext();

  const storageKey = `iap-dashboard-url-${incidentId}`;
  const savedParams = useIapParams(incidentId || '');
  const hydratedRef = useRef(false);

  const getValues = useCallback((): IapParams => {
    return savedParams || DEFAULT_IAP_PARAMS;
  }, [savedParams]);

  const [phase, setPhase] = useState(() => getValues().phase ?? 0);

  const persistParams = useCallback(
    (next: { formsView: FormsView; phase: number; mode?: string }) => {
      const nextMode = next.mode ?? formsViewToMode(next.formsView);
      const uiSearchParams = validateAndCreateIapSearchParams(
        incrementParamsForUI({
          mode: nextMode,
          phase: next.phase,
          formsView: next.formsView,
        }),
        searchParams
      );
      setSearchParams(uiSearchParams);

      const codeSearchParams = validateAndCreateIapSearchParams(
        {
          mode: nextMode,
          phase: next.phase,
          formsView: next.formsView,
        },
        searchParams
      );

      if (incidentId) {
        const newUrl = location.pathname + '?' + codeSearchParams.toString();
        localStorage.setItem(storageKey, newUrl);
      }
    },
    [searchParams, setSearchParams, incidentId, location.pathname, storageKey]
  );

  const updatePhase = useCallback(
    (newPhase: number) => {
      setPhase(newPhase);
      persistParams({ formsView, phase: newPhase });
    },
    [formsView, persistParams]
  );

  const updateFormsView = useCallback(
    (nextView: FormsView) => {
      setFormsView(nextView);
      setMode(formsViewToMode(nextView));
      persistParams({ formsView: nextView, phase });
    },
    [setFormsView, setMode, persistParams, phase]
  );

  const updateMode = useCallback(
    (newMode: string) => {
      const nextView = resolveFormsView({
        formsViewParam: newMode === 'future' ? 'working' : null,
        modeParam: newMode,
        currentPeriodNumber,
      });
      updateFormsView(nextView);
    },
    [currentPeriodNumber, updateFormsView]
  );

  // Hydrate once from URL / storage when incident + period are known.
  // Planning-P is Working-only; coerce numeric/historical formsView to working.
  useEffect(() => {
    if (!incidentId || hydratedRef.current) return;
    const initial = getValues();
    const resolved = resolveFormsView({
      formsViewParam:
        initial.formsView !== undefined
          ? serializeFormsView(initial.formsView)
          : searchParams.get('formsView'),
      modeParam: initial.mode ?? searchParams.get('mode'),
      currentPeriodNumber,
    });
    const workingView: FormsView = 'working';
    setFormsView(workingView);
    setMode(formsViewToMode(workingView));
    setPhase(initial.phase ?? 0);
    hydratedRef.current = true;
    if (resolved !== 'working') {
      persistParams({ formsView: workingView, phase: initial.phase ?? 0 });
    }
  }, [
    incidentId,
    getValues,
    searchParams,
    currentPeriodNumber,
    setFormsView,
    setMode,
    persistParams,
  ]);

  // Keep in sync when formsView query changes externally (e.g. notification link).
  // Always keep Planning-P on working.
  useEffect(() => {
    const fromUrl = searchParams.get('formsView');
    if (!fromUrl) return;
    const resolved = resolveFormsView({
      formsViewParam: fromUrl,
      modeParam: searchParams.get('mode'),
      currentPeriodNumber,
    });
    if (resolved === 'working') {
      setFormsView('working');
      setMode(formsViewToMode('working'));
    } else if (hydratedRef.current) {
      updateFormsView('working');
    }
  }, [
    searchParams,
    currentPeriodNumber,
    setFormsView,
    setMode,
    updateFormsView,
  ]);

  useEffect(() => {
    if (targetPhase !== null) {
      updatePhase(targetPhase);
      setTargetPhase(null);
    }
  }, [targetPhase, setTargetPhase, updatePhase]);

  return (
    <PlanningDashboard
      updatePhase={updatePhase}
      updateMode={updateMode}
      updateFormsView={updateFormsView}
      currentPhase={phase}
      currentMode={formsViewToMode(formsView)}
      formsView={formsView}
    />
  );
};

export default IAPDashboardNew;
